import {PolymerElement, html} from '@polymer/polymer/polymer-element.js';
import '@polymer/paper-datatable-api/paper-datatable-api.js';
import '@polymer/paper-datatable-api/paper-datatable-api-column.js';

let timer;

/**
 * This is an element that is used to display arrays of data from json files or objects in a tabular form. This acts as
 * an extention to the `paper-datatable-api` element to further suit a specific need of simply displaying data.
 *
 * Currently the element will take the required information, form tables from the property names and fill the rows with the matching data for such.
 * This element will also allow for different pagination options, as it is enabled in this element we allow for the user to decide on the
 * maximum amount of records to show per page.
 *
 * This element currently serves as a cutdown simple to use version of `paper-datatable-api` as we found the element quite fiddly to use
 * and hard to easily implement.
 *
 *
 * @customElement
 * @polymer
 * @demo demo/index.html
 */
class CbarDatatable extends PolymerElement {
  static get template() {
    return html`
        <style>
            :host {
                display: block;
            }

            [hidden] {
                display: none;
            }

            paper-datatable-api {
                --paper-datatable-api-checked-checkbox-color: var(--cbar-sys-color-primary, #004CA2);
                --paper-datatable-api-tr-hover-background-color: #e8e8e8;
                overflow: scroll;
                visibility: visible;
            }
        </style>
        <paper-datatable-api data="[[_displayedData]]" position-sort-icon="right" paginate on-sort="_handleSort" id="datatable" page="{{_page}}" available-size="[[availableSizes]]" size="{{_size}}" total-elements="[[_totalTransactions]]" total-pages="[[_totalPages]]" selectable$="[[selectable]]">
            <template is="dom-repeat" items="[[tableColumnNames]]" as="column">
                <paper-datatable-api-column draggable-column="" sortable="" header="[[column.name]]" property="[[column.property]]">
                    <template>
                        {{value}}
                    </template>
                </paper-datatable-api-column>
            </template>
            <slot on-slotchange="_checkVisibility" id="action-column" hidden=""></slot>
        </paper-datatable-api>
    `;
  }

  static get is() {
    return 'cbar-datatable';
  }

  static get properties() {
    return {
      /**
       * This is property used for testing purposes, also outlines the name of the element to enable testing to be done on properties of the element
       */
      _name: {
        type: String,
        value: 'cbar-datatable'
      },
      /**
       * determines the page that the pagination starts on by default, as we dont see a use behind it being anything other than 0 to start with, this is the default
       */
      _page: {
        type: Number,
        value: 0,
      },
      /**
       * value that allows for the determination of pages and how many pages should the data be split into
       */
      _size: {
        type: Number,
        value: 10,
      },
      /**
       * data is the data that the user wishes to parse to the element, this is normally done through the iron-ajax element, however if the user wishes to simply parse in a
       * json object, they are able to do such as well
       */
      data: {
        type: Array,
        observer: "_calculateColumns",
      },
      /**
       * this allows for the user to determine the options for pagination in regards to how many items should be displaying per page
       */
      availableSizes: {
        type: Array,
        value: ['10', '25', '50']
      },
      selectable: {
        type: Boolean,
        value: false
      }
    };
  }

  static get observers() {
    return [
      '_debouncePaginate(data, _page, _size)',
    ];
  }

  ready() {
    super.ready();
    requestAnimationFrame(() => {
      this.setupStylingOverwrite();
    })
  }

  /**
   * Returns the datatable, useful to be able to get the rows from such
   *
   * @returns {*}
   */
  get dataTable() {
    if (!this.$.dataTable) {
      this.$.dataTable = this.root.querySelector('#datatable');
    }
    return this.$.dataTable;
  }

  get dataTableFooter() {
    if (!this.__dataTableFooter) {
      this.__dataTableFooter = this.dataTable.root.querySelector('paper-datatable-api-footer');
    }
    return this.__dataTableFooter;
  }

  /**
   * returns the rows within the datatable, calls on the `get dataTable` function
   *
   * @returns {*|NodeList<Element>|NodeListOf<Polymer.Element>|any}
   */
  get tableRows() {
    return this.dataTable.root.querySelectorAll('.paper-datatable-api-tr');
  }

  /**
   * Allows us to run a paginate function but only once per animation frame, this is handy if, as an example, you were
   * on the second page of pagination and you changed the page size from 5 to 10. this would trigger _paginateData to get called
   * twice which would cause a race conditon.
   *
   * @param _data
   * @param _page
   * @param _size
   * @protected
   */
  _debouncePaginate(_data, _page, _size) {
    if (!_data || _data.error) {
      return;
    }

    this._paginate(_data)
  }


  /**
   * Allows for the pagination of the table, along with this, the method determines the total amount of pages
   * from the total amount of data divided by the size.
   *
   * This also sets the offset of the data which gives an offset to the data, giving us the data we expect from pagination
   *
   * @param _data
   * @protected
   */
  _paginate(_data) {
    this._totalTransactions = _data.length;
    this._totalPages = Math.ceil(this._totalTransactions / this._size);

    let offset = this._page * this._size;

    this._displayedData = _data.slice(offset, offset + this._size);
  }

  /**
   * Allows for us to use the sort function built into the `paper-datatable-api` element, as there is no sorting function by default
   * and only a listener, we have created a function to handle this within the element.
   *
   * @param e
   * @protected
   */
  _handleSort(e) {
    let sortDetails = e.detail.sort;

    let property = sortDetails.property || 'paymentDate';
    let direction = sortDetails.direction || 'desc';

    let sortFn = this.constructor._sortGeneric;

    if (property === 'paymentDate' || property === "date") {
      sortFn = this.constructor._sortDate;
    }


    this.transactionData = this.data;
    this.data = [];


    this.transactionData.forEach((transaction) => {
      this.push('data', transaction);
    });

    this.data.sort((a, b) => {
      let result = sortFn(a[property], b[property]);
      return direction === 'desc' ? (result > 0 ? -1 : 1) : result;
    });
  }

  /**
   * Acts as a generic comparison for the element to sort generic values with, we have a seperate function for date value
   */
  static _sortGeneric(a, b) {
    return a.localeCompare(b);
  }

  /**
   * Compares two values against each other in a date format to determine which value is higher than the other
   *
   * @param a
   * @param b
   * @returns {number}
   * @protected
   */
  static _sortDate(a, b) {
    return new Date(a) - new Date(b);
  }

  /**
   * calculates the columns based on the passed properties.
   * For example, if 2 properties with the name "name" and another "description" this would create 2 columns for the data to fill
   *
   * this will then call of the `_processHeaders` function in order to give the headers based on the property name
   * @protected
   */
  _calculateColumns() {
    if (this.data.length > 0) {
      let column = this.data[0];
      let columnProps = Object.getOwnPropertyNames(column);

      this._processHeaders(columnProps);

      // Trigger the _setColumns call to ensure that
      // our data gets processed
      // TODO: Figure out why we needed this at some point??
      // this.dataTable._setColumns();

      // we need to convert any custom HTML from an injected string to HTML
      //TODO: figure out a better timing fix for this
      setTimeout(() => {
        let columnChildren = this.dataTable.root.querySelectorAll('td:not(.selectable) > *');

        columnChildren.forEach(column => {
          // nodeType 3 === Text, if the column doesnt have HTML in it, then we convert, if it does, we dont do it
          if (column.childNodes.length !== 1 && !column.innerText.trim()) {
            return;
          }

          column.innerHTML = column.innerText;
        })

      }, 200);

    }

  }

  /**
   * This will determine the header name based off the property, if the property is 1 word, it will capitalise such
   * and give that as the header name, if it is in camelcase, it will split the word into its respective words
   *
   * ie testWords will become Test Words
   *
   * @param columnProps
   * @protected
   */
  _processHeaders(columnProps) {
    this.tableColumnNames = [];

    columnProps.forEach(columnProp => {
      let capitalColumn = columnProp.charAt(0).toUpperCase() + columnProp.slice(1);
      let columnName = capitalColumn.replace(/([A-Z])/g, ' $1').trim();

      if (this.existsInSlot(columnName)) {
        return;
      }

      this.tableColumnNames.push({
        name: columnName,
        property: columnProp,
      });

    });

  }

  _checkVisibility(e) {
    let actionColumns = this.root.querySelector('#action-column');
    actionColumns.hidden = !actionColumns.assignedNodes().length;
  }

  setupStylingOverwrite() {
    this.dataTableFooter.root.querySelector('.end-justified').style.borderTop = '0';
    this.dataTable.root.querySelector('table').style.borderBottom = '1px solid rgb(223, 223, 223)';
  }

  existsInSlot(columnName) {
    let actionColumns = this.root.querySelector('#action-column').assignedNodes();

    return !!actionColumns.find((node) => {
      return node.nodeType === 1 && node.getAttribute('property') === columnName.toLowerCase();
    });
  }
}

window.customElements.define(CbarDatatable.is, CbarDatatable);
